import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { type ThemeMode, useTheme } from './theme-provider';

const OPTIONS: { mode: ThemeMode; label: string; icon: typeof Sun }[] = [
  { mode: 'light', label: 'Light', icon: Sun },
  { mode: 'dark', label: 'Dark', icon: Moon },
  { mode: 'system', label: 'System', icon: Monitor },
];

/** Icon shown on the trigger: the resolved theme, except `system` always shows the monitor. */
function TriggerIcon({
  mode,
  resolvedTheme,
}: {
  mode: ThemeMode;
  resolvedTheme: 'light' | 'dark';
}) {
  if (mode === 'system') return <Monitor className="size-4" aria-hidden="true" />;
  return resolvedTheme === 'dark' ? (
    <Moon className="size-4" aria-hidden="true" />
  ) : (
    <Sun className="size-4" aria-hidden="true" />
  );
}

/**
 * Sun / moon / monitor menu: system, light or dark, remembered in `localStorage` (ADR-0022).
 * Drop into either app's header next to the user menu.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { mode, resolvedTheme, setMode } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={className}
          aria-label={`Colour mode: ${mode} (currently ${resolvedTheme})`}
        >
          <TriggerIcon mode={mode} resolvedTheme={resolvedTheme} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {OPTIONS.map(({ mode: optionMode, label, icon: Icon }) => (
          <DropdownMenuItem key={optionMode} onSelect={() => setMode(optionMode)}>
            <Icon className="size-4" aria-hidden="true" />
            {label}
            {mode === optionMode ? <Check className="ml-auto size-4" aria-hidden="true" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
